import { gql } from '@apollo/client'

const TICKET_ATTRIBUTES = gql`
  fragment ticketAttributes on Ticket {
    id
    createdAt
    updatedAt
    expiry
    number
    latestReviewStatus
    reviews {
      reviewStatus
    }
    obtained
    stateIssued
    countryIssued
    ticketTemplate {
      id
      shortName
      name
      ticketFileConfigs {
        name
        id
      }
    }
    worker {
      id
      user {
        firstName
        lastName
      }
      subcontractors {
        id
        company {
          name
        }
      }
    }
    files {
      id
      name
      file {
        name
        key
        url
        contentType
      }
    }
  }
`
const TICKET_ATTRIBUTES_SUBSET = gql`
  fragment ticketAttributesSubset on Ticket {
    id
    updatedAt
    expiry
    number
    builderGenerated
    latestReviewStatus

    ticketTemplate {
      shortName
      name
    }
    worker {
      id
      user {
        firstName
        lastName
      }
      subcontractors {
        id
        company {
          name
        }
      }
    }
  }
`

const TICKET_REVIEW_ATTRIBUTES = gql`
  fragment ticketReviewAttributes on TicketReview {
    id
    createdAt
    updatedAt

    adminUser {
      id
      user {
        firstName
        lastName
      }
    }

    reviewStatus
    reviewComments
  }
`

export const GET_TICKETS_BY_WORKERS_CSV = gql`
  query ticketsByWorkersCsv_UI(
    $filter: TicketFilterInput
    $searchString: String
  ) {
    ticketsByWorkers(filter: $filter, searchString: $searchString) {
      ...ticketAttributes
    }
  }
  ${TICKET_ATTRIBUTES}
`

// Big search for tickets, should always be used with a strict filter
export const GET_TICKETS = gql`
  query tickets_UI(
    $filter: TicketFilterInput
    $pagination: PaginationInput
    $sort: TicketSortInput
    $searchString: String
  ) {
    ticketsByWorkers(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...ticketAttributes
    }
  }
  ${TICKET_ATTRIBUTES}
`

// Subset of tickets data, used for fast list view
export const GET_TICKETS_BY_WORKERS_LIST = gql`
  query ticketsByWorkersList_UI(
    $filter: TicketFilterInput
    $pagination: PaginationInput
    $sort: TicketSortInput
    $searchString: String
  ) {
    ticketsByWorkers(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      ...ticketAttributesSubset
    }
    ticketsByWorkersAggregation(
      filter: $filter
      pagination: $pagination
      sort: $sort
      searchString: $searchString
    ) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${TICKET_ATTRIBUTES_SUBSET}
`

export const GET_TICKETS_BY_WOKRER_FOR_SEARCH = gql`
  query ticketsByWorkersForSearch_UI(
    $filter: TicketFilterInput
    $searchString: String
  ) {
    ticketsByWorkers(filter: $filter, searchString: $searchString) {
      ...ticketAttributesSubset
    }
  }
  ${TICKET_ATTRIBUTES_SUBSET}
`

export const GET_TICKET_TEMPLATES_LIST = gql`
  query ticketTemplatesList_UI {
    ticketTemplates {
      id
      name
      shortName
      code
      expiry
      obtained
      number
      medical
      ticketCategory {
        id
        name
      }
      ticketType {
        id
        name
      }
      ticketFileConfigs {
        id
        name
        required
      }
      ticketCount
    }
  }
`

export const GET_TICKET_TEMPLATES_INFO = gql`
  query ticketTemplatesInfo_UI {
    ticketTypes {
      id
      name
      ticketTemplatesCount
    }
    ticketCategories {
      id
      name
      ticketTemplatesCount
    }
  }
`

const TICKET_TEMPLATE_ATTRIBUTES = gql`
  fragment ticketTemplateAttributes on TicketTemplate {
    id
    createdAt
    updatedAt

    shortName
    name

    code
    expiry
    obtained
    number
    ticketCategory {
      id
      name
    }
    ticketType {
      id
      name
    }
    ticketFileConfigs {
      id
      name
      required
    }
  }
`

export const GET_TICKETS_TEMPLATES = gql`
  query ticketTemplates_UI {
    ticketTemplates {
      ...ticketTemplateAttributes
    }
  }
  ${TICKET_TEMPLATE_ATTRIBUTES}
`

export const GET_TICKETS_TEMPLATES_FOR_SEARCH = gql`
  query ticketTemplatesForSearch_UI {
    ticketTemplates {
      id
      shortName
    }
  }
`

export const CREATE_TICKET_TEMPLATE = gql`
  mutation createTicketTemplate_UI($input: CreateTicketTemplateInput!) {
    createTicketTemplate(input: $input) {
      id
    }
  }
`

export const CREATE_TICKET_REVIEW = gql`
  mutation createTicketReview_UI($input: CreateTicketReviewInput!) {
    createTicketReview(input: $input) {
      ...ticketReviewAttributes
    }
  }
  ${TICKET_REVIEW_ATTRIBUTES}
`

export const CREATE_TICKET_REVIEWS = gql`
  mutation createTicketReviews_UI($input: [CreateTicketReviewInput]!) {
    createTicketReviews(input: $input) {
      ...ticketReviewAttributes
    }
  }
  ${TICKET_REVIEW_ATTRIBUTES}
`

export const GET_TICKET = gql`
  query ticket_UI($id: String!) {
    ticket(id: $id) {
      ...ticketAttributes
    }
  }
  ${TICKET_ATTRIBUTES}
`

export const GET_TICKET_LATEST_REVIEW = gql`
  query ticketLatestReview_UI($ticketId: String!) {
    latestTicketReview(ticketId: $ticketId) {
      createdAt
      reviewStatus
      reviewComments
      adminUser {
        user {
          firstName
          lastName
        }
      }
    }
  }
`

export const CREATE_TICKET_FOR_WORKER = gql`
  mutation createTicketForWorker_UI($input: CreateTicketInputV2!) {
    createTicketv2(input: $input) {
      ...ticketAttributes
    }
  }
  ${TICKET_ATTRIBUTES}
`

export const UPDATE_TICKET_FOR_WORKER = gql`
  mutation updateTicketForWorker_UI($id: String!, $input: UpdateTicketInputV2) {
    updateTicketv2(id: $id, input: $input) {
      ...ticketAttributes
    }
  }
  ${TICKET_ATTRIBUTES}
`

export const DELETE_TICKET_FOR_WORKER = gql`
  mutation deleteTicketForWorker_UI($id: String!) {
    deleteTicket(id: $id) {
      id
    }
  }
`
